import Card from "components/card";
import WithoutPageTable from "components/table/WithoutPageTable";
import { createColumnHelper } from "@tanstack/react-table";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import AddItemsModal from "./AddItemsModal";
import AssignOrderModal from "./AssignOrderModal";
import SplitItemModal from "./SplitItemModal";
import RevertItemModal from "./RevertItemModal";
import Button from "components/button";
import { CONTAINER_ITEM_MAPPING_STATUS } from "constants";
import UnAssignOrderModal from "./UnAssignOrderModal";
import { Link } from "react-router-dom";
import { MdCheckCircle, MdOutlineError } from "react-icons/md";
import ExportPdfButton from "./ExportPdfButton";
import DeleteItemModal from "./DeleteItemModal";
import { ImagePreview } from "components/modal/ImagePreview";

const columnHelper = createColumnHelper();

function ItemsTable({ refreshList }) {
  const { containerDetailsData } = useSelector((state) => state.containers);
  const { container: containerPharm = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );

  const containerItemsData = useMemo(
    () =>
      containerDetailsData.items
        ?.filter((item) => item._id)
        ?.map((item) => item) ?? [],
    [containerDetailsData]
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const [openModal, setOpenModal] = useState(null); // "assign" | "unassign" | "split" | "revert"
  const columns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row?.order?.orderNo, {
        id: "orderNo",
        header: "ORDER NO",
        enableSorting: true,
        enableHiding: false,
        cell: (info) => {
          const row = info.row.original;
          const orderId = row?.order?._id;
          const orderNo = info.getValue();
          const userName = `${row?.order?.orderForInfo?.firstname || ""} ${
            row?.order?.orderForInfo?.lastname || ""
          }`.trim();

          return orderId ? (
            <div className="flex flex-col">
              <Link
                to={`/orders/${orderId}`}
                className="cursor-pointer text-blue-600 hover:underline"
              >
                {orderNo || "N/A"}
              </Link>
              {userName && <span className="text-sm">{userName}</span>}
            </div>
          ) : (
            "N/A"
          );
        },
      }),

      // IMAGE
      columnHelper.accessor("picture", {
        id: "image",
        header: "IMG",
        enableSorting: false,
        enableHiding: false,
        cell: ({ getValue }) => <ImagePreview url={getValue()} />,
      }),

      // BASIC FIELDS
      columnHelper.accessor("name", {
        id: "name",
        header: "NAME",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("marka", {
        id: "marka",
        header: "MARKA",
        enableSorting: false,
        enableHiding: false,

        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("description", {
        id: "description",
        header: "DESCRIPTION",
        enableSorting: false,
        cell: (info) => (
          <span className="block max-w-[200px] truncate">
            {info.getValue() || "-"}
          </span>
        ),
      }),

      // QUANTITY FIELDS
      columnHelper.accessor("ctn", {
        id: "ctn",
        header: "CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("qty", {
        id: "qty",
        header: "QTY/CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("kg", {
        id: "kg",
        header: "KG/CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("cbm", {
        id: "cbm",
        header: "CBM/CTN",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),

      // TOTALS
      columnHelper.accessor("totalQty", {
        id: "totalQty",
        header: "TOTAL QTY",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalKg", {
        id: "totalKg",
        header: "TOTAL KG",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalCBM", {
        id: "totalCBM",
        header: "TOTAL CBM",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("rate", {
        id: "rate",
        header: "RATE",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),
      columnHelper.accessor("totalAmount", {
        id: "totalAmount",
        header: "TOTAL AMOUNT",
        enableSorting: false,
        cell: (info) => info.getValue() ?? 0,
      }),

      // STATUS

      columnHelper.accessor((row) => row.mappingStatus?.enumVal || "", {
        id: "mappingStatus",
        header: "STATUS",
        cell: (info) => {
          const status = info.row.original.mappingStatus;
          if (!status) return "-";

          const statusKey = status.enumVal;
          const statusName = status.name || "-";

          return (
            <div className="flex items-center">
              {statusKey === CONTAINER_ITEM_MAPPING_STATUS.CLEARED ? (
                <MdCheckCircle className="me-1 text-green-500 dark:text-green-300" />
              ) : statusKey === CONTAINER_ITEM_MAPPING_STATUS.SUSPENSE ? (
                <MdOutlineError className="me-1 text-amber-500 dark:text-amber-300" />
              ) : null}

              <p className="text-sm font-bold text-navy-700 dark:text-white">
                {statusName}
              </p>
            </div>
          );
        },
      }),

      columnHelper.display({
        id: "actions",
        header: "ACTIONS",
        enableHiding: false,

        cell: ({ row }) => {
          const { mappingStatus, parentId, isParentAssign } = row.original;

          return (
            <div className="flex gap-2">
              {mappingStatus?.enumVal !==
              CONTAINER_ITEM_MAPPING_STATUS.CLEARED ? (
                <>
                  {/* Assign */}
                  {containerPharm?.assign && (
                    <Button
                      className="border-green-600 bg-green-600 py-1 text-sm hover:bg-green-700 active:bg-green-800 dark:border-green-500 dark:bg-green-500 dark:hover:bg-green-600 dark:active:bg-green-600"
                      onClick={() => {
                        setSelectedItem(row.original);
                        setOpenModal("assign");
                      }}
                    >
                      Assign
                    </Button>
                  )}

                  {/* Split */}
                  {parentId === null && containerPharm?.split && (
                    <Button
                      className="py-1 text-sm"
                      onClick={() => {
                        setSelectedItem(row.original);
                        setOpenModal("split");
                      }}
                    >
                      Split
                    </Button>
                  )}

                  {/* Revert */}
                  {!isParentAssign &&
                    parentId !== null &&
                    containerPharm?.revert && (
                      <Button
                        className="border-orange-600 bg-orange-600 py-1 text-sm hover:bg-orange-700 active:bg-orange-800 dark:border-orange-500 dark:bg-orange-500 dark:hover:bg-orange-600 dark:active:bg-orange-600"
                        onClick={() => {
                          setSelectedItem(row.original);
                          setOpenModal("revert");
                        }}
                      >
                        Revert
                      </Button>
                    )}
                </>
              ) : (
                // Only Unassign
                containerPharm?.unAssign && (
                  <Button
                    className="border-red-600 bg-red-600 py-1 text-sm hover:bg-red-700 active:bg-red-800 dark:border-red-500 dark:bg-red-500 dark:hover:bg-red-600 dark:active:bg-red-600"
                    onClick={() => {
                      setSelectedItem(row.original);
                      setOpenModal("unassign");
                    }}
                  >
                    Unassign
                  </Button>
                )
              )}

              {/* Delete */}
              {containerPharm?.itemDelete && (
                <DeleteItemModal
                  data={row.original}
                  refreshList={refreshList}
                />
              )}
            </div>
          );
        },
      }),
    ];
  }, []);
  const initialColumnVisibility = {
    orderNo: containerPharm?.orderColumn ? true : false,
    image: true,
    name: true,
    marka: true,
    description: false,
    ctn: true,
    qty: true,
    kg: false,
    cbm: false,
    totalQty: true,
    totalKg: false,
    totalCBM: false,
    rate: false,
    totalAmount: false,
    mappingStatus: containerPharm?.statusColumn ? true : false,
    actions: containerPharm?.itemAction ? true : false,
  };

  return (
    <>
      {openModal === "assign" && (
        <AssignOrderModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}

      {openModal === "unassign" && (
        <UnAssignOrderModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}

      {openModal === "split" && (
        <SplitItemModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}

      {openModal === "revert" && (
        <RevertItemModal
          isOpen={true}
          onClose={() => {
            setSelectedItem(null);
            setOpenModal(null);
          }}
          item={selectedItem}
          refreshList={refreshList}
        />
      )}

      <Card extra={`w-full h-full sm:overflow-auto px-6`}>
        <div className="mb-5 w-full flex-col items-center lg:pl-0">
          <WithoutPageTable
            tableTitle={`Loaded Items : ${
              containerDetailsData?.containerNo ?? "N/A"
            }`}
            actionButton={
              <>
                <ExportPdfButton />
                {containerPharm?.itemAdd && (
                  <AddItemsModal refreshList={refreshList} />
                )}
              </>
            }
            columns={columns}
            data={containerItemsData ?? []}
            initialColumnVisibility={initialColumnVisibility}
            isColumnDropdown={true}
          />
        </div>
      </Card>
    </>
  );
}

export default ItemsTable;
